import { useState } from "react";
import { toast } from "sonner";
import { X, Stack, Copy, CheckCircle, XCircle } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";

export default function BulkDialog({ domains, onClose, onCreated }) {
  const [text, setText] = useState("");
  const [campaign, setCampaign] = useState("");
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const urls = text.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);

  const submit = async () => {
    if (urls.length === 0) { toast.error("Paste at least one URL"); return; }
    setLoading(true);
    try {
      const payload = { urls };
      if (campaign.trim()) payload.campaign = campaign.trim();
      if (domain) payload.domain = domain;
      const { data } = await api.post("/links/bulk", payload);
      setResults(data);
      const created = data.results.filter((r) => r.ok).map((r) => r.link);
      if (created.length) onCreated(created);
      toast.success(`${data.created} created, ${data.failed} failed`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const copyAll = async () => {
    const links = (results?.results || []).filter((r) => r.ok).map((r) => r.short_url).join("\n");
    try { await navigator.clipboard.writeText(links); toast.success("All short links copied"); }
    catch { toast.error("Copy blocked — select the links and copy manually"); }
  };

  return (
    <div className="fixed inset-0 bg-black/85 flex items-center justify-center p-4 sm:p-6 z-50" onClick={onClose}>
      <div className="w-full max-w-2xl border border-white/15 bg-[#0a0a0a] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="bulk-dialog">
        <div className="sticky top-0 bg-[#0a0a0a] border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2"><Stack size={18} weight="bold" /><h2 className="font-head text-lg font-bold">Bulk import</h2></div>
          <button onClick={onClose} data-testid="close-bulk-btn" className="text-zinc-500 hover:text-white transition-colors"><X size={18} /></button>
        </div>

        <div className="p-6">
          {!results ? (
            <>
              <p className="label-xs mb-2">// one url per line (max 100)</p>
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} data-testid="bulk-textarea"
                placeholder={"https://example.com/one\nhttps://example.com/two\nexample.com/three"}
                className="w-full bg-transparent border border-white/10 p-3 text-sm mono placeholder:text-zinc-600 focus:outline-none focus:border-white transition-colors mb-4" />
              <div className="grid sm:grid-cols-2 gap-3 mb-5">
                <div>
                  <span className="label-xs block mb-2">Domain</span>
                  <select value={domain} onChange={(e) => setDomain(e.target.value)} data-testid="bulk-domain-select"
                    className="w-full h-11 bg-[#0a0a0a] border border-white/10 px-3 text-sm mono text-zinc-300 focus:outline-none focus:border-white transition-colors">
                    <option value="">Default</option>
                    {domains.map((d) => <option key={d.domain} value={d.domain}>{d.domain}</option>)}
                  </select>
                </div>
                <div>
                  <span className="label-xs block mb-2">Campaign <span className="text-zinc-700">(opt)</span></span>
                  <input value={campaign} onChange={(e) => setCampaign(e.target.value)} placeholder="launch-q3" data-testid="bulk-campaign-input"
                    className="w-full h-11 bg-transparent border border-white/10 px-3 text-sm mono placeholder:text-zinc-600 focus:outline-none focus:border-white transition-colors" />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="mono text-xs text-zinc-500">{urls.length} url{urls.length !== 1 ? "s" : ""} detected</span>
                <button onClick={submit} disabled={loading || urls.length === 0} data-testid="bulk-submit-btn"
                  className="h-11 px-6 bg-white text-black font-head font-bold text-sm uppercase tracking-wider flex items-center gap-2 hover:bg-zinc-300 transition-colors disabled:opacity-50">
                  <Stack size={15} weight="bold" /> {loading ? "creating..." : `Shorten ${urls.length}`}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4 mono text-xs">
                  <span className="text-green-400 flex items-center gap-1"><CheckCircle size={14} />{results.created} created</span>
                  {results.failed > 0 && <span className="text-red-400 flex items-center gap-1"><XCircle size={14} />{results.failed} failed</span>}
                </div>
                <div className="flex gap-2">
                  <button onClick={copyAll} data-testid="bulk-copy-all-btn" className="h-9 px-4 border border-white/20 mono text-xs uppercase tracking-wider flex items-center gap-2 hover:border-white transition-colors"><Copy size={14} /> Copy all</button>
                  <button onClick={() => { setResults(null); setText(""); }} data-testid="bulk-again-btn" className="h-9 px-4 bg-white text-black mono text-xs uppercase tracking-wider hover:bg-zinc-300 transition-colors">New batch</button>
                </div>
              </div>
              <div className="border border-white/10" data-testid="bulk-results">
                {results.results.map((r, i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-2 border-b border-white/10 last:border-b-0">
                    {r.ok ? <CheckCircle size={15} className="text-green-500 shrink-0" /> : <XCircle size={15} className="text-red-500 shrink-0" />}
                    <span className="mono text-xs text-zinc-500 truncate flex-1">{r.input}</span>
                    {r.ok
                      ? <a href={r.short_url} target="_blank" rel="noreferrer" className="mono text-xs text-white hover:underline shrink-0">/r/{r.code}</a>
                      : <span className="mono text-[11px] text-red-400 shrink-0 max-w-[240px] truncate" title={r.error}>{r.error}</span>}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
